// deno-lint-ignore-file no-explicit-any

export type GetPreparedKey = "clothes" | "manners" | "vocabulary" | "books" | "music" | "cinema";

export type GetPreparedJson = {
  clothes_md?: string | null;
  manners_md?: string | null;
  vocabulary_md?: string | null;
  books_md?: string | null;
  music_md?: string | null;
  cinema_md?: string | null;
};

export type SourceLink = { title: string; uri: string };

export function sanitizeGetPreparedJson(raw: unknown): Required<GetPreparedJson> {
  const obj = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as any) : {};
  const read = (k: keyof GetPreparedJson) => {
    const v = typeof obj?.[k] === "string" ? String(obj[k]) : "";
    return v.trim() ? v : null;
  };
  return {
    clothes_md: read("clothes_md"),
    manners_md: read("manners_md"),
    vocabulary_md: read("vocabulary_md"),
    books_md: read("books_md"),
    music_md: read("music_md"),
    cinema_md: read("cinema_md"),
  };
}

export function splitMdAtSources(md: string): { main: string; sources: SourceLink[] } {
  const text = String(md || "").trim();
  if (!text) return { main: "", sources: [] };
  const lines = text.split(/\r?\n/);
  const startIdx = lines.findIndex((l) => {
    const t = String(l || "").trim();
    if (!t) return false;
    if (/^#{1,6}\s*sources\b/i.test(t)) return true;
    return t.toLowerCase() === "sources";
  });
  if (startIdx < 0) return { main: text, sources: [] };

  const main = lines.slice(0, startIdx).join("\n").trim();
  const rest = lines.slice(startIdx + 1);
  const sources: SourceLink[] = [];
  const seen = new Set<string>();

  for (const raw of rest) {
    const line = String(raw || "").trim();
    if (!line) continue;
    const cleaned = line.replace(/^\s*[-*•]\s*/, "").trim();
    if (!cleaned) continue;
    const mdLink = cleaned.match(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/i);
    const urlMatch = cleaned.match(/https?:\/\/\S+/i);
    const uri = (mdLink ? mdLink[2] : urlMatch?.[0]) || "";
    if (!uri) continue;
    const safeUri = uri.replace(/[)\].,;]+$/, "");
    if (seen.has(safeUri)) continue;
    const title = (mdLink ? mdLink[1] : cleaned.replace(uri, "").trim()) || safeUri;
    sources.push({ title: String(title).trim().slice(0, 140) || safeUri, uri: safeUri });
    seen.add(safeUri);
    if (sources.length >= 8) break;
  }

  return { main, sources };
}

export function parseMarkdownTable(md: string): { headers: string[]; rows: string[][] } {
  const text = String(md || "").trim();
  if (!text) return { headers: [], rows: [] };
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const tableLines = lines.filter((l) => l.startsWith("|") && l.includes("|"));
  if (tableLines.length < 3) return { headers: [], rows: [] };

  const splitLine = (line: string) =>
    line
      .split("|")
      .map((p) => p.trim())
      .filter((_, idx, arr) => !(idx === 0 || idx === arr.length - 1))
      .map((p) => p.replace(/\\\|/g, "|").trim());

  const headers = splitLine(tableLines[0]!);
  const rows: string[][] = [];
  for (let i = 2; i < tableLines.length; i += 1) {
    const parts = splitLine(tableLines[i]!);
    if (parts.length >= 2) rows.push(parts);
    if (rows.length >= 30) break;
  }
  return { headers, rows };
}

export function splitListCell(text: string, maxItems = 10): string[] {
  const t = String(text || "").trim();
  if (!t) return [];
  const cleaned = t.replace(/\s+/g, " ");
  const parts = cleaned
    .split(/[;•·]/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length >= 2) return parts.slice(0, maxItems);
  const comma = cleaned
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (comma.length >= 2) return comma.slice(0, maxItems);
  return [cleaned.slice(0, 140)];
}

export function buildMarkdownTable(headers: string[], rows: string[][]): string {
  const safeHeaders = (headers || []).map((h) => String(h || "").trim()).filter(Boolean);
  if (safeHeaders.length < 2) return "";
  const normRow = (r: string[]) =>
    safeHeaders.map((_, idx) => String(r?.[idx] ?? "").replace(/\|/g, "\\|").trim());
  const safeRows = (rows || [])
    .map((r) => normRow(r))
    .filter((r) => r.some((cell) => String(cell || "").trim()));
  if (!safeRows.length) return "";

  const head = `| ${safeHeaders.join(" | ")} |`;
  const sep = `| ${safeHeaders.map(() => "---").join(" | ")} |`;
  const body = safeRows.map((r) => `| ${r.join(" | ")} |`).join("\n");
  return [head, sep, body].join("\n").trim();
}

export function formatSourcesBlock(sources: SourceLink[]): string {
  const list = (sources || [])
    .map((s) => ({
      title: String(s?.title || "").trim(),
      uri: String(s?.uri || "").trim(),
    }))
    .filter((s) => s.uri)
    .slice(0, 8);
  if (!list.length) return "";
  const lines = list.map((s) => `- [${s.title || s.uri}](${s.uri})`);
  return ["Sources", ...lines].join("\n").trim();
}
